import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { companyInfo } from '../mock';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/sonner';
import { toast } from 'sonner';
import { ParallaxShapes } from '../components/ParallaxShapes';
import { useSEO } from '../hooks/useSEO';

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Ihre Nachricht wurde erfolgreich versendet!', {
          description: 'Wir werden uns in Kürze bei Ihnen melden.'
        });
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        toast.error('Fehler beim Senden', {
          description: data.detail || 'Bitte versuchen Sie es später erneut.'
        });
      }
    } catch (err) {
      toast.error('Verbindungsfehler', {
        description: 'Bitte überprüfen Sie Ihre Internetverbindung.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useSEO('contact');

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-gray-50 via-white to-gray-100">
      {/* Parallax Shapes Background */}
      <ParallaxShapes scrollY={scrollY} />
      
      <Toaster position="top-right" />
      
      {/* Content Container */}
      <div className="relative" style={{ zIndex: 2 }}>
        {/* Hero Section */}
        <section className="pt-32 pb-20 bg-[#264F77] text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-5xl md:text-6xl font-normal mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                Kontakt
              </h1>
              <p className="text-xl text-gray-200 leading-relaxed">
                Wir freuen uns auf ein unverbindliches Gespräch über Ihre Immobilienentwicklung, den Kauf oder Verkauf von Wohnungen und Renditeobjekten.
              </p>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-24 bg-white/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-normal text-[#264F77] mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>
                Senden Sie uns eine Nachricht
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264F77] focus:border-transparent transition-all"
                    placeholder="Ihr vollständiger Name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    E-Mail *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264F77] focus:border-transparent transition-all"
                    placeholder="ihre.email@beispiel.ch"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Telefon
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264F77] focus:border-transparent transition-all"
                    placeholder="+41 71 123 45 67"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Nachricht *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264F77] focus:border-transparent transition-all resize-none"
                    placeholder="Teilen Sie uns mit, wie wir Ihnen helfen können..."
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center px-8 py-4 bg-[#264F77] text-white font-semibold rounded-md hover:bg-[#1e3d5c] transition-all duration-300 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    'Wird gesendet...'
                  ) : (
                    <>
                      Nachricht senden
                      <Send className="ml-2" size={20} />
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-normal text-[#264F77] mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>
                Kontaktinformationen
              </h2>
              <div className="space-y-8 mb-12">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#264F77]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-[#264F77]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#264F77] mb-2">Adresse</h3>
                    <p className="text-gray-600">
                      <span className="font-semibold">LIVAG AG</span><br />
                      {companyInfo.address}<br />
                      {companyInfo.city}
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#264F77]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="text-[#264F77]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#264F77] mb-2">Telefon</h3>
                    <a
                      href={`tel:${companyInfo.phone}`}
                      className="text-gray-600 hover:text-[#264F77] transition-colors"
                    >
                      {companyInfo.phone}
                    </a>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#264F77]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="text-[#264F77]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#264F77] mb-2">E-Mail</h3>
                    <a
                      href={`mailto:${companyInfo.email}`}
                      className="text-gray-600 hover:text-[#264F77] transition-colors"
                    >
                      {companyInfo.email}
                    </a>
                  </div>
                </div>
              </div>

              {/* Google Maps */}
              <div className="rounded-lg overflow-hidden shadow-lg">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2699.2451234866603!2d9.374777976382871!3d47.42666347117393!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x479b1fe6aa498187%3A0x8fbd5f53cfd61df1!2sLIVAG%20AG!5e0!3m2!1sde!2sch!4v1776357517050!5m2!1sde!2sch"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="LIVAG AG Standort – Katharinengasse 4, 9000 St. Gallen"
                  className="w-full"
                  data-testid="google-maps-embed"
                ></iframe>
                <div className="bg-[#264F77] text-white p-4">
                  <p className="font-semibold">LIVAG AG</p>
                  <p className="text-sm text-gray-200">Katharinengasse 4, 9000 St. Gallen</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      </div>
    </div>
  );
};
